import React from 'react';
import {
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Paper,
  Divider,
  useTheme,
  Grid,
} from '@mui/material';
import Button from './Button';
import {
  TextFields as TextIcon,
  FlipToFront as FlipIcon,
  AccountTree as LabyrinthIcon,
  Info as InfoIcon,
  Memory as MemoryIcon,
  Cloud as CloudIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import Card from './Card';
import { useTranslation } from 'react-i18next';

// Компонент для красивого отображения JSON с контрастными цветами
const JsonPreview = ({ data }) => {
  const formatJSON = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <Box
      component="div"
      sx={{
        backgroundColor: 'background.default',
        padding: '15px',
        borderRadius: '4px',
        overflowX: 'auto',
        color: 'text.primary',
        fontFamily: 'monospace',
        fontSize: '14px',
        lineHeight: '1.4',
        margin: '10px 0',
      }}
    >
      <pre style={{ margin: 0 }}>{formatJSON(data)}</pre>
    </Box>
  );
};

// Component to help course creators select content type
const ContentTypeSelector = ({
  selectedType,
  onChange,
  isForBlock = false,
  onSwitchToBlocks,
  onSwitchToSingle,
  isMultiBlockMode = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('lessons');

  const contentTypes = [
    {
      value: 'standard',
      label: t('contentTypes.standard.label'),
      description: t('contentTypes.standard.description'),
      icon: <TextIcon fontSize="large" />,
      example: t('contentTypes.standard.example'),
    },
    {
      value: 'presentation',
      label: t('contentTypes.presentation.label'),
      description: t('contentTypes.presentation.description'),
      icon: <CodeIcon fontSize="large" />,
      example: t('contentTypes.presentation.example'),
    },
    {
      value: 'fullHtml',
      label: t('contentTypes.fullHtml.label'),
      description: t('contentTypes.fullHtml.description'),
      icon: <CodeIcon fontSize="large" />,
      example: t('contentTypes.fullHtml.example'),
    },
    {
      value: 'htmlBlock',
      label: t('contentTypes.htmlBlock.label'),
      description: t('contentTypes.htmlBlock.description'),
      icon: <CodeIcon fontSize="large" />,
      example: t('contentTypes.htmlBlock.example'),
    },
    {
      value: 'video',
      label: t('contentTypes.video.label'),
      description: t('contentTypes.video.description'),
      icon: <VideocamIcon fontSize="large" />,
      example: t('contentTypes.video.example'),
    },
    {
      value: 'flippingCards',
      label: t('contentTypes.flippingCards.label'),
      description: t('contentTypes.flippingCards.description'),
      icon: <FlipIcon fontSize="large" />,
      example: t('contentTypes.flippingCards.example'),
    },
    {
      value: 'labyrinth',
      label: t('contentTypes.labyrinth.label'),
      description: t('contentTypes.labyrinth.description'),
      icon: <LabyrinthIcon fontSize="large" />,
      example: t('contentTypes.labyrinth.example'),
    },
    {
      value: 'memoryGame',
      label: t('contentTypes.memoryGame.label'),
      description: t('contentTypes.memoryGame.description'),
      icon: <MemoryIcon fontSize="large" />,
      example: t('contentTypes.memoryGame.example'),
    },
    {
      value: 'tagCloud',
      label: t('contentTypes.tagCloud.label'),
      description: t('contentTypes.tagCloud.description'),
      icon: <CloudIcon fontSize="large" />,
      example: t('contentTypes.tagCloud.example'),
    },
    {
      value: 'rolePlayGame',
      label: t('contentTypes.rolePlayGame.label'),
      description: t('contentTypes.rolePlayGame.description'),
      icon: <PersonIcon fontSize="large" />,
      example: t('contentTypes.rolePlayGame.example'),
    },
  ];

  const handleChange = (event) => {
    onChange(event.target.value);
  };

  const selectedTypeInfo = contentTypes.find(
    (type) => type.value === selectedType
  );

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {!isForBlock && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {t('contentTypeSelector.selectContentType')}
          </Typography>
          {isMultiBlockMode && onSwitchToSingle && (
            <Button
              variant="outlined"
              size="small"
              onClick={onSwitchToSingle}
            >
              {t('lessonEditor.switchToSingle')}
            </Button>
          )}
          {!isMultiBlockMode && onSwitchToBlocks && (
            <Button
              variant="outlined"
              size="small"
              onClick={onSwitchToBlocks}
            >
              {t('contentTypeEditor.switchToBlocks')}
            </Button>
          )}
        </Box>
      )}

      <Grid container spacing={1.5}>
        {contentTypes.map((type) => (
          <Grid item xs={6} sm={4} md={3} key={type.value}>
            <Card
              component="div"
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                p: 2,
                backgroundColor: theme.palette.mode === 'dark' ? '#2d3748' : '#ffffff',
                borderColor:
                  selectedType === type.value
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                borderWidth: selectedType === type.value ? 2 : 1,
                '&:hover': {
                  boxShadow: 2,
                  borderColor: theme.palette.primary.light,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => onChange(type.value)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    color:
                      selectedType === type.value
                        ? theme.palette.primary.main
                        : (theme.palette.mode === 'dark' ? '#3b82f6' : theme.palette.text.secondary),
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  {React.cloneElement(type.icon, { 
                    style: { width: 20, height: 20 } 
                  })}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color:
                        selectedType === type.value
                          ? theme.palette.primary.main
                          : (theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary),
                      mb: 0.5,
                    }}
                  >
                    {type.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      lineHeight: 1.3,
                      color: theme.palette.mode === 'dark' ? '#cbd5e0' : theme.palette.text.secondary,
                    }}
                  >
                    {type.description}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!isForBlock && selectedTypeInfo && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '16px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <InfoIcon sx={{ mr: 1, color: theme.palette.info.main }} />
            <Typography variant="subtitle1">{t('contentTypeSelector.instructions')}</Typography>
          </Box>

          {selectedType === 'standard' && (
            <Typography variant="body2">{t('contentTypeSelector.standardInstructions')}</Typography>
          )}

          {selectedType === 'presentation' && (
            <Typography variant="body2">{t('contentTypeSelector.presentationInstructions')}</Typography>
          )}

          {selectedType === 'fullHtml' && (
            <Typography variant="body2">{t('contentTypeSelector.fullHtmlInstructions')}</Typography>
          )}

          {selectedType === 'htmlBlock' && (
            <Typography variant="body2">{t('contentTypeSelector.htmlBlockInstructions')}</Typography>
          )}

          {selectedType === 'video' && (
            <Box>
              <Typography variant="body2">{t('contentTypeSelector.videoInstructions')}</Typography>
            </Box>
          )}

          {selectedType === 'flippingCards' && (
            <Box>
              <Typography variant="body2" paragraph>
                {t('contentTypeSelector.flippingCardsInstructions')}
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>{t('contentTypeSelector.howItWorks')}:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>
                • {t('contentTypeSelector.flippingCardsInstructions1')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>
                • {t('contentTypeSelector.flippingCardsInstructions2')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>
                • {t('contentTypeSelector.flippingCardsInstructions3')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, pl: 2 }}>
                • {t('contentTypeSelector.flippingCardsInstructions4')}
              </Typography>
            </Box>
          )}

          {selectedType === 'labyrinth' && (
            <Typography variant="body2" component="div">
              {t('contentTypeSelector.labyrinthInstructions')}
              <JsonPreview
                data={{
                  title: 'Основы JavaScript',
                  startNode: {
                    id: 'start',
                    title: 'Введение',
                    content:
                      '<p>Добро пожаловать в JavaScript! Выберите путь:</p>',
                    options: [
                      { text: 'Изучить переменные', nextNodeId: 'variables' },
                      { text: 'Изучить функции', nextNodeId: 'functions' },
                    ],
                  },
                  nodes: [
                    {
                      id: 'variables',
                      title: 'Переменные',
                      content:
                        '<p>Переменные JavaScript - это контейнеры для хранения значений данных.</p>',
                      options: [
                        {
                          text: 'Перейти к типам данных',
                          nextNodeId: 'datatypes',
                        },
                      ],
                    },
                    {
                      id: 'functions',
                      title: 'Функции',
                      content:
                        '<p>JavaScript-функции - это блоки кода, предназначенные для выполнения определенной задачи.</p>',
                      isEndNode: true,
                    },
                  ],
                }}
              />
            </Typography>
          )}

          {selectedType === 'memoryGame' && (
            <Typography variant="body2" component="div">
              {t('contentTypeSelector.memoryGameInstructions')}
              <JsonPreview
                data={[
                  { text: 'JavaScript', pairId: 1 },
                  { text: 'ECMAScript', pairId: 1 },
                  { text: 'HTML', pairId: 2 },
                  { text: 'Markup Language', pairId: 2 },
                  { text: 'CSS', pairId: 3 },
                  { text: 'Styling Language', pairId: 3 },
                ]}
              />
            </Typography>
          )}

          {selectedType === 'tagCloud' && (
            <Typography variant="body2" component="div">
              {t('contentTypeSelector.tagCloudInstructions')}
              <JsonPreview
                data={[
                  {
                    term: 'JavaScript',
                    definition:
                      'Популярный язык программирования для веб-разработки',
                  },
                  {
                    term: 'HTML',
                    definition: 'Язык разметки для создания веб-страниц',
                  },
                  {
                    term: 'CSS',
                    definition: 'Язык стилей для оформления веб-страниц',
                  },
                  {
                    term: 'React',
                    definition:
                      'Библиотека JavaScript для создания пользовательских интерфейсов',
                  },
                  {
                    term: 'Node.js',
                    definition: 'Среда выполнения JavaScript вне браузера',
                  },
                ]}
              />
            </Typography>
          )}

          {selectedType === 'rolePlayGame' && (
            <Typography variant="body2" component="div">
              {t('contentTypeSelector.rolePlayGameInstructions')}
              <JsonPreview
                data={{
                  character: {
                    name: 'Профессор Кодов',
                    avatar: 'https://via.placeholder.com/100',
                    role: 'Ученый-программист',
                    description: 'Ваш наставник в мире программирования',
                  },
                  scenario: [
                    {
                      type: 'dialog',
                      text: 'Приветствую в интерактивном курсе программирования! Я профессор Кодов.',
                    },
                    {
                      type: 'choice',
                      question: 'С какой темы вы хотели бы начать изучение?',
                      choices: [
                        { text: 'Переменные и типы данных', nextScene: 3 },
                        { text: 'Функции', nextScene: 6 },
                      ],
                    },
                  ],
                }}
              />
            </Typography>
          )}
        </Paper>
      )}

      <FormControl sx={{ display: 'none' }}>
        <RadioGroup
          name="content-type-selector"
          value={selectedType}
          onChange={handleChange}
        >
          {contentTypes.map((type) => (
            <FormControlLabel
              key={type.value}
              value={type.value}
              control={<Radio />}
              label={type.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default ContentTypeSelector;
